import { useCallback, useEffect, useRef, useState } from 'react';
import { Feed, SavedList } from './components/Feed';
import { Masthead } from './components/Masthead';
import { ProfilePane } from './components/ProfilePane';
import { QuizForm } from './components/QuizForm';
import { TabBar, type Tab } from './components/TabBar';
import { DEFAULT_MIN_SCORE, type Deal, type DealStatus } from './shared/deals';
import type { Profile } from './shared/profile';

interface ProfileResponse {
  profile: Profile | null;
  updatedAt?: string;
}

// The server answers errors as JSON ({ error }, status >= 400). Parsing that
// body into state would crash the next render, so throw on !ok instead.
async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export default function App() {
  const [tab, setTab] = useState<Tab>('feed');
  const [profile, setProfile] = useState<Profile | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [feed, setFeed] = useState<Deal[]>([]);
  const [saved, setSaved] = useState<Deal[]>([]);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [minScore, setMinScore] = useState(DEFAULT_MIN_SCORE);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((message: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = setTimeout(() => setToast(null), 6000);
  }, []);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const body = await fetchJson<ProfileResponse>('/api/profile');
      setProfile(body.profile);
      setUpdatedAt(body.updatedAt);
    } catch (e) {
      setLoadError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- load only setStates after awaiting the fetch (and in finally); intentional fetch-on-mount
    load();
  }, [load]);

  const loadDeals = useCallback(async (cut: number) => {
    setDealsLoading(true);
    try {
      const [newDeals, savedDeals] = await Promise.all([
        fetchJson<{ deals: Deal[] }>(`/api/deals?status=new&minScore=${cut}&limit=50`),
        fetchJson<{ deals: Deal[] }>('/api/deals?status=saved&limit=100'),
      ]);
      setFeed(newDeals.deals);
      setSaved(savedDeals.deals);
    } catch (e) {
      // A failed refresh must not unmount a working feed — surface it and keep
      // whatever is already on screen.
      setToast(`Couldn't refresh: ${errMsg(e)}`);
    } finally {
      setDealsLoading(false);
    }
  }, []);

  // Only fetch deals once there is a brief to have ranked them against.
  useEffect(() => {
    if (!profile) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- loadDeals only setStates after awaiting fetches (and in finally)
    loadDeals(minScore);
  }, [profile, minScore, loadDeals]);

  const saveProfile = useCallback(async (next: Profile) => {
    setSaving(true);
    setSaveError(null);
    try {
      const body = await fetchJson<ProfileResponse>('/api/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      setProfile(body.profile);
      setUpdatedAt(body.updatedAt);
      setEditing(false);
    } catch (e) {
      setSaveError(errMsg(e));
    } finally {
      setSaving(false);
    }
  }, []);

  // Optimistic: the card leaves the list immediately, because on a phone the
  // round-trip is long enough to feel like the tap didn't register. A failure
  // puts it back exactly where it was and says so.
  const setDealStatus = useCallback(
    async (deal: Deal, status: DealStatus) => {
      const previousFeed = feed;
      const previousSaved = saved;
      setBusyId(deal.id);
      setFeed((list) => list.filter((d) => d.id !== deal.id));
      setSaved((list) =>
        status === 'saved'
          ? [{ ...deal, status }, ...list.filter((d) => d.id !== deal.id)]
          : list.filter((d) => d.id !== deal.id),
      );
      try {
        await fetchJson<{ deal: Deal }>(`/api/deals/${deal.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status }),
        });
      } catch (e) {
        setFeed(previousFeed);
        setSaved(previousSaved);
        showToast(`Couldn't update: ${errMsg(e)}`);
      } finally {
        setBusyId(null);
      }
    },
    [feed, saved, showToast],
  );

  if (loading) {
    return (
      <div className="min-h-screen">
        <Masthead edition={new Date()} />
        <p className="px-5 pt-10 text-center font-mono text-xs tracking-wide text-ink-faint">
          Setting the type…
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen">
        <Masthead edition={new Date()} />
        <section className="px-5 py-10">
          <div className="mx-auto max-w-2xl">
            <p className="kicker">Wire down</p>
            <h2 className="display-sm mt-2 text-2xl">Couldn&rsquo;t reach the desk</h2>
            <p className="mt-3 text-sm text-ink-soft">{loadError}</p>
            <button
              type="button"
              onClick={() => {
                setLoading(true);
                load();
              }}
              className="mt-5 rounded-xs bg-ink px-4 py-2 text-sm text-paper hover:bg-accent"
            >
              Try again
            </button>
          </div>
        </section>
      </div>
    );
  }

  // First run: there is nothing to rank against until the brief exists, so the
  // quiz takes the whole screen — no tabs to wander off into.
  if (!profile) {
    return (
      <div className="min-h-screen">
        <Masthead edition={new Date()} />
        <QuizForm initial={null} onSave={saveProfile} saving={saving} saveError={saveError} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      <Masthead edition={new Date()} />

      <main>
        {tab === 'feed' && (
          <Feed
            deals={feed}
            loading={dealsLoading}
            minScore={minScore}
            onMinScore={setMinScore}
            onStatus={setDealStatus}
            busyId={busyId}
          />
        )}
        {tab === 'saved' && (
          <SavedList
            deals={saved}
            loading={dealsLoading}
            onStatus={setDealStatus}
            busyId={busyId}
          />
        )}
        {tab === 'profile' &&
          (editing ? (
            <QuizForm
              initial={profile}
              onSave={saveProfile}
              onCancel={() => {
                setSaveError(null);
                setEditing(false);
              }}
              saving={saving}
              saveError={saveError}
            />
          ) : (
            <ProfilePane profile={profile} updatedAt={updatedAt} onEdit={() => setEditing(true)} />
          ))}
      </main>

      {toast && (
        <div
          role="status"
          className="fixed inset-x-4 bottom-20 z-20 mx-auto max-w-md border border-accent bg-card px-4 py-2.5 text-sm text-ink shadow-sm"
        >
          {toast}
        </div>
      )}

      <TabBar active={tab} onChange={setTab} />
    </div>
  );
}
